import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const values = [
  {
    title: 'Quality First',
    description: 'We source only the finest materials and employ rigorous quality control to ensure every pillow meets our exacting standards.',
  },
  {
    title: 'Sleep Science',
    description: 'Our products are developed in collaboration with sleep specialists and orthopedic experts for optimal support.',
  },
  {
    title: 'Sustainability',
    description: 'We are committed to eco-friendly practices, using responsibly sourced materials and sustainable packaging.',
  },
  {
    title: 'Customer Happiness',
    description: 'Your satisfaction is our priority. Our 100-night trial and dedicated support team ensure a risk-free experience.',
  },
];

const stats = [
  { number: '50,000+', label: 'Happy Sleepers' },
  { number: '4.8', label: 'Average Rating' },
  { number: '100', label: 'Night Trial' },
  { number: '5', label: 'Year Warranty' },
];

function About() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  letterSpacing: 3,
                  fontWeight: 600,
                }}
              >
                Our Story
              </Typography>
              <Typography variant="h1" sx={{ mt: 1, mb: 3 }}>
                Better Sleep
                <br />
                Starts Here
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '1.1rem', opacity: 0.9, lineHeight: 1.8 }}
              >
                Sleepout was born from a simple belief: everyone deserves a great 
                night's sleep. Founded in 2020, we set out to create pillows that 
                combine premium comfort with innovative sleep science.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80"
                alt="Comfortable bedroom"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 6, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?w=800&q=80"
                alt="Premium pillow"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.dark',
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                Our Mission
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 3 }}>
                Redefining Sleep Comfort
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                We believe that quality sleep shouldn't be a luxury. Our mission 
                is to make premium sleep products accessible to everyone, without 
                compromising on quality or sustainability.
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Every Sleepout pillow is carefully crafted using the finest materials, 
                from organic cotton covers to responsibly sourced down and innovative 
                memory foam technologies. We work closely with sleep experts to ensure 
                our products provide optimal support and comfort.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                When you choose Sleepout, you're not just buying a pillow—you're 
                investing in better sleep, better mornings, and a better you.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.dark',
                letterSpacing: 2,
                fontWeight: 600,
              }}
            >
              What We Stand For
            </Typography>
            <Typography variant="h2" sx={{ mt: 1 }}>
              Our Values
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={value.title}>
                <Box
                  sx={{
                    bgcolor: 'white',
                    p: 4,
                    borderRadius: 2,
                    height: '100%',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'primary.light',
                      opacity: 0.3,
                      fontSize: '3rem',
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    0{index + 1}
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                    {value.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {value.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ mb: 2, color: 'white' }}>
              Ready for Better Sleep?
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 4, opacity: 0.9, fontSize: '1.1rem' }}
            >
              Experience the Sleepout difference with our 100-night risk-free trial.
            </Typography>
            <Button
              component={RouterLink}
              to="/products"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Shop Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default About;
